import React from 'react';
import Type from 'prop-types';
import { childrenOfType } from 'airbnb-prop-types';
import { TableCell } from '@material-ui/core';
import cn from 'classnames';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import { ROW_HEIGHT } from 'utils/constants';
import Column from '../Column';
import useStyles from './DataGrid.styles';

const DEFAULT_HEIGHT = 300;

const DataGrid = props => {
  const classes = useStyles();
  const { height, dataRowCount, children, ...other } = props;
  const rowCount = dataRowCount + 1; // data rows count + header row
  const columns = React.Children.toArray(children);
  const columnCount = columns.length;

  const getColumnWidth = ({ index }) => {
    return columns[index].props.width;
  };

  const createHeader = headerData => {
    const { column, key, style: cellStyle } = headerData;
    const { headerClassName, headerStyle } = props;

    const { columnData, dataKey, headerRenderer: headerRendererProp, label } = column.props;

    const classNames = cn(headerClassName, column.props.headerClassName);
    const style = { ...cellStyle, ...headerStyle, ...column.props.style };

    const renderedHeader = headerRendererProp({
      columnData,
      dataKey,
      label,
    });

    return (
      <TableCell
        key={key}
        component="div"
        className={classNames}
        style={style}
        size="small"
        variant="head"
      >
        {renderedHeader}
      </TableCell>
    );
  };

  const headerRenderer = cellRendererParams => {
    const { key, columnIndex, style } = cellRendererParams;
    const column = columns[columnIndex];

    return createHeader({ column, key, style });
  };

  const createColumn = cellRendererParams => {
    const { column, columnIndex, isScrolling, parent, rowData, rowIndex } = cellRendererParams;

    const { cellDataGetter, cellRenderer, columnData, dataKey, formatter } = column.props;
    const cellData = cellDataGetter({ columnData, dataKey, rowData, columnIndex, formatter });
    const renderedCell = cellRenderer({
      cellData,
      columnData,
      columnIndex,
      dataKey,
      isScrolling,
      parent,
      rowData,
      rowIndex,
    });

    return renderedCell;
  };

  const bodyRenderer = cellRendererParams => {
    const { rowGetter } = props;
    const { key, columnIndex, isScrolling, parent, rowIndex: index, style } = cellRendererParams;
    const column = columns[columnIndex];
    const rowData = rowGetter({ index: index - 1 });
    const classNames = cn('textOverflow', column.props.classNames);

    const createdColumn = createColumn({
      column,
      columnIndex,
      isScrolling,
      parent,
      rowData,
      rowIndex: index,
    });

    return (
      <TableCell
        key={key}
        component="div"
        className={classNames}
        style={style}
        size="small"
        variant="body"
      >
        {createdColumn}
      </TableCell>
    );
  };

  const cellRenderer = cellRendererParams => {
    const { rowIndex } = cellRendererParams;

    return rowIndex === 0 ? headerRenderer(cellRendererParams) : bodyRenderer(cellRendererParams);
  };

  return (
    <AutoSizer disableHeight>
      {({ width }) => {
        return (
          <MultiGrid
            {...other}
            width={width}
            height={height}
            rowCount={rowCount}
            columnCount={columnCount}
            rowHeight={ROW_HEIGHT}
            columnWidth={getColumnWidth}
            cellRenderer={cellRenderer}
            classNameTopLeftGrid={classes.topLeftGrid}
            classNameTopRightGrid={classes.topRightGrid}
            classNameBottomLeftGrid={classes.bottomLeftGrid}
          />
        );
      }}
    </AutoSizer>
  );
};

DataGrid.propTypes = {
  children: childrenOfType(Column).isRequired,
  dataRowCount: Type.number.isRequired,
  rowGetter: Type.func.isRequired,
  height: Type.number,
  fixedRowCount: Type.number,
  fixedColumnCount: Type.number,
  scrollToColumn: Type.number,
  scrollToRow: Type.number,
  enableFixedColumnScroll: Type.bool,
  enableFixedRowScroll: Type.bool,
  hideTopRightGridScrollbar: Type.bool,
  hideBottomLeftGridScrollbar: Type.bool,
  style: Type.object, // eslint-disable-line react/forbid-prop-types
  headerClassName: Type.string,
  headerStyle: Type.object, // eslint-disable-line react/forbid-prop-types
};

DataGrid.defaultProps = {
  scrollToColumn: 0,
  scrollToRow: 0,
  height: DEFAULT_HEIGHT,
  fixedRowCount: 1,
  fixedColumnCount: 0,
  enableFixedColumnScroll: true,
  enableFixedRowScroll: true,
  hideTopRightGridScrollbar: true,
  hideBottomLeftGridScrollbar: true,
  style: {},
  headerClassName: null,
  headerStyle: {},
};

export default DataGrid;
